import { getUnixTime, startOfDay, subDays } from 'date-fns'
import { TFunction } from 'i18next'
import { FC, SVGAttributes } from 'react'
import { match } from 'ts-pattern'
import Bicycle from 'assets/icons/nucleo/18px_bicycle.svg'
import Directions from 'assets/icons/nucleo/18px_directions.svg'
import FaceSmile from 'assets/icons/nucleo/18px_face-smile-closed-eyes.svg'
import ShirtSport from 'assets/icons/nucleo/18px_shirt-sport.svg'
import ShoeSneakers from 'assets/icons/nucleo/18px_shoe-sneakers.svg'
import Ticket from 'assets/icons/nucleo/18px_ticket-2.svg'
import UserCap from 'assets/icons/nucleo/18px_user-cap.svg'
import WaterSurface from 'assets/icons/nucleo/18px_water-surface.svg'
import { defaultSortBy, searchClient } from 'containers/meilisearchClient'
import { raceCategoriesLevel1 } from 'helpers/raceCategoryLevels'
import { recordKeys } from 'helpers/records'
import { AutocompleteItem } from 'interfaces/algolia'

const buildLevel2FiltersString = (
  editionLiveLevel2CategoryKeys: readonly string[]
) =>
  editionLiveLevel2CategoryKeys
    .map((key) => `editionLiveLevel2CategoryKey=${key}`)
    .join(' OR ')

const todayTimestamp = () => getUnixTime(startOfDay(Date.now()))

const hitsPerPage = 12

const attributesToRetrieve = [
  'eventCity',
  'eventLocationData',
  'eventName',
  'eventSlug',
  'objectID',
  'eventCountrySubdivisionDisplayCodeLevel2',
  'eventRegion',
  'eventCountry',
  'editionLiveStartDateTimestamp',
  'editionLiveEndDateTimestamp',
  'nbHits',
  'editionLiveLevel2Category',
  'eventLiveStatus',
  'editionCalendarStatus',
  'eventLivePriceStartingFrom',
  'eventLiveDistanceKm',
  'editionLiveLevel1CategoryKey',
  'eventCoverImage',
  'eventIsFeatured',
  'editionLiveLevel2CategoryKey'
]

const allLevel2 = {
  runningLevel2Categories: buildLevel2FiltersString(
    raceCategoriesLevel1.RUNNING.level2Categories
  ),
  trailLevel2Categories: buildLevel2FiltersString(
    raceCategoriesLevel1.TRAIL.level2Categories
  ),
  triathlonLevel2Categories: buildLevel2FiltersString(
    raceCategoriesLevel1.TRIATHLON.level2Categories
  ),
  funLevel2Categories: buildLevel2FiltersString(
    raceCategoriesLevel1.FUN.level2Categories
  ),
  walkLevel2Categories: buildLevel2FiltersString(
    raceCategoriesLevel1.WALK.level2Categories
  ),
  cyclingLevel2Categories: buildLevel2FiltersString(
    raceCategoriesLevel1.CYCLING.level2Categories
  ),
  otherLevel2Categories: buildLevel2FiltersString(
    raceCategoriesLevel1.OTHER.level2Categories
  )
}

type LevelOneCategory = keyof typeof raceCategoriesLevel1

type Icon = FC<SVGAttributes<SVGElement>>

const getHits = (
  results: ({ hits: AutocompleteItem[] } | {})[],
  index: number
): AutocompleteItem[] =>
  results[index] &&
  'hits' in results[index] &&
  Array.isArray(results[index].hits)
    ? results[index].hits
    : []

export interface HomeEvents {
  featuredEvents: { all: AutocompleteItem[] } & {
    [key in LevelOneCategory]: AutocompleteItem[]
  }
  recommendedEvents: AutocompleteItem[]
  finishedEvents: AutocompleteItem[]
}

export interface FeaturedEventDisplay {
  key: 'all' | LevelOneCategory
  label: string
  Icon: Icon
}

interface IHomeService {
  getFeaturedEvents: () => Promise<HomeEvents>
  getFeaturedEventsDisplay: (
    t: TFunction<'common'>,
    withAll?: boolean
  ) => FeaturedEventDisplay[]
}

export const HomeService: IHomeService = {
  getFeaturedEvents: async () => {
    const filters =
      `eventIsFeatured="true" AND eventStatus="LIVE" AND editionLiveEndDateTimestamp>=${todayTimestamp()}` as const

    const response = await searchClient.search<AutocompleteItem>([
      {
        indexName: defaultSortBy,
        params: {
          filters: `isRecommended="true"`,
          attributesToRetrieve,
          attributesToHighlight: ['objectID']
        }
      },
      {
        indexName: defaultSortBy,
        params: {
          hitsPerPage,
          filters,
          attributesToRetrieve,
          attributesToHighlight: ['objectID']
        }
      },
      {
        indexName: defaultSortBy,
        params: {
          hitsPerPage,
          filters: `${filters} AND (${allLevel2.runningLevel2Categories})`,
          attributesToRetrieve,
          attributesToHighlight: ['objectID']
        }
      },
      {
        indexName: defaultSortBy,
        params: {
          hitsPerPage,
          filters: `${filters} AND (${allLevel2.trailLevel2Categories})`,
          attributesToRetrieve,
          attributesToHighlight: ['objectID']
        }
      },
      {
        indexName: defaultSortBy,
        params: {
          hitsPerPage,
          filters: `${filters} AND (${allLevel2.triathlonLevel2Categories})`,
          attributesToRetrieve,
          attributesToHighlight: ['objectID']
        }
      },
      {
        indexName: defaultSortBy,
        params: {
          hitsPerPage,
          filters: `${filters} AND (${allLevel2.funLevel2Categories})`,
          attributesToRetrieve,
          attributesToHighlight: ['objectID']
        }
      },
      {
        indexName: defaultSortBy,
        params: {
          hitsPerPage,
          filters: `${filters} AND (${allLevel2.walkLevel2Categories})`,
          attributesToRetrieve,
          attributesToHighlight: ['objectID']
        }
      },
      {
        indexName: defaultSortBy,
        params: {
          hitsPerPage,
          filters: `${filters} AND (${allLevel2.cyclingLevel2Categories})`,
          attributesToRetrieve,
          attributesToHighlight: ['objectID']
        }
      },
      {
        indexName: defaultSortBy,
        params: {
          hitsPerPage,
          filters: `${filters} AND (${allLevel2.otherLevel2Categories})`,
          attributesToRetrieve,
          attributesToHighlight: ['objectID']
        }
      },
      {
        indexName: defaultSortBy,
        params: {
          filters: `eventIsFeatured="true" AND eventStatus="LIVE" AND editionLiveStartDateTimestamp>=${getUnixTime(
            startOfDay(subDays(Date.now(), 2))
          )} AND editionLiveEndDateTimestamp<${getUnixTime(
            startOfDay(Date.now())
          )}`,
          attributesToRetrieve,
          attributesToHighlight: ['objectID']
        }
      }
    ])

    return {
      recommendedEvents: getHits(response.results, 0),
      featuredEvents: {
        all: getHits(response.results, 1),
        RUNNING: getHits(response.results, 2),
        TRAIL: getHits(response.results, 3),
        TRIATHLON: getHits(response.results, 4),
        FUN: getHits(response.results, 5),
        WALK: getHits(response.results, 6),
        CYCLING: getHits(response.results, 7),
        OTHER: getHits(response.results, 8)
      },
      finishedEvents: getHits(response.results, 9)
    }
  },
  getFeaturedEventsDisplay: (t: TFunction<'common'>, withAll?: boolean) => [
    ...(withAll
      ? [
          {
            key: 'all' as const,
            label: t('all'),
            Icon: Ticket as Icon
          }
        ]
      : []),
    ...recordKeys(raceCategoriesLevel1).map((value) => ({
      key: value,
      label: t(value),
      Icon: match(value)
        .with('RUNNING', () => UserCap as Icon)
        .with('TRAIL', () => ShirtSport as Icon)
        .with('TRIATHLON', () => WaterSurface as Icon)
        .with('FUN', () => FaceSmile as Icon)
        .with('WALK', () => ShoeSneakers as Icon)
        .with('CYCLING', () => Bicycle as Icon)
        .with('OTHER', () => Directions as Icon)
        .exhaustive()
    }))
  ]
}
